/**
 * MSW Overrides: Billing Receipt — Campaign discount scenario (CVO-19186)
 *
 * Returns a single receipt with a `discount` total so the Discount row in
 * ReceiptPreview and the PDF can be verified locally.
 *
 * The receipt reflects a 10% campaign reduction applied at the invoice level:
 * - items carry their full catalog gross price
 * - `subtotal` holds the pre-discount net/gross amounts
 * - `total.net` and `total.gross` are already net of the discount
 * - `discount` holds the negative saving as net/gross (CVO-19087 contract)
 *
 * Enable by setting ENABLE_MSW=true and
 * NUXT_PUBLIC_MSW_OVERRIDES_BILLING_RECEIPT_DISCOUNT=true in your .env,
 * then restart the dev server.
 */

import type { HttpHandler } from 'msw';
import { http, HttpResponse } from 'msw';
import { BFF_ENDPOINTS, toMSWPattern, createMockReceipt } from '@cvo/domain';
import type { ReceiptResponse } from '@cvo/domain';

export function createBillingReceiptDiscountOverrides(baseUrl: string): HttpHandler[] {
  const sanitizedBaseUrl = baseUrl.replace(/\/$/, '');

  const discountedReceipt: ReceiptResponse = createMockReceipt({
    subtotal: {
      net: { amount: '48.76', currency: 'EUR' },
      gross: { amount: '59.00', currency: 'EUR' },
    },
    discount: {
      net: { amount: '-4.88', currency: 'EUR' },
      gross: { amount: '-5.90', currency: 'EUR' },
    },
    total: {
      net: { amount: '43.88', currency: 'EUR' },
      gross: { amount: '53.10', currency: 'EUR' },
    },
    additionalCharges: [
      {
        type: 'VAT',
        code: 'VAT_EUR',
        rate: { value: '21', type: 'PERCENTAGE' },
        amount: { amount: '9.22', currency: 'EUR' },
      },
    ],
    paid: { amount: '53.10', currency: 'EUR' },
    refunded: { amount: '0.00', currency: 'EUR' },
    balance: { amount: '0.00', currency: 'EUR' },
  });

  return [
    http.get(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.RECEIPT)}`,
      () => HttpResponse.json(discountedReceipt),
    ),
  ];
}
